import SimpleTimeChart from "../../../component/instance/chart/SimpleTimeChart";
import { useContext, useMemo, useState } from "react";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import ActivityApi from "../../../api/instance/ActivityApi";
import SqlApi from "../../../api/instance/SqlApi";

// Helpers
import ConditionalRender from "../../../helpers/ConditionalRender";
import Helper from "../../../helpers/Helper";

// Types
import Time from "../../../types/instance/Time";
import { INSTANCE_TYPE_SQLSERVER } from "../../../component/Constants";

const GenericActivity = (props: {
    instanceId: number,
    instanceType: string,
    statementId: number,
    apiString: string,
}) => {
    const timeRangeContext = useContext(TimeRangeContext)

    const [statementActivity, setStatementActivity] = useState<number[]>([]);
    const [groupedStatementActivity, setGroupedStatementActivity] = useState<number[]>([]);
    const [batchActivity, setBatchActivity] = useState<number[]>([]);
    const [groupedBatchActivity, setGroupedBatchActivity] = useState<number[]>([]);

    const [timeChartLoading, setTimeChartLoading] = useState<boolean>(true);
    const [sqlChartLoading, setSqlChartLoading] = useState<boolean>(true);

    const [statisticsTimeSeries, setStatisticsTimeSeries] = useState<number[]>([]);
    const [executionsSeries, setExecutionsSeries] = useState<number[]>([]);

    const [groupedStatisticsTimeSeries, setGroupedStatisticsTimeSeries] = useState<number[]>([]);
    const [groupedExecutionsSeries, setGroupedExecutionsSeries] = useState<number[]>([]);

    const fetchStatementActivity = async(isGroupStatement: boolean, isGroupBatch: boolean) => {
        try {
            let responses;
            if (props.instanceType === INSTANCE_TYPE_SQLSERVER) {
                responses = await Promise.all([
                    !isGroupStatement && ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&sqlhash=${props.statementId}`),
                    isGroupStatement && ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&grouphash=${props.statementId}`),
                    !isGroupBatch && ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&batchsqlhash=${props.statementId}`),
                    isGroupBatch && ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&batchgrouphash=${props.statementId}`),
                ]);
            } else {
                responses = await Promise.all([
                    !isGroupStatement && ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&sqlhash=${props.statementId}`),
                    isGroupStatement && ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&grouphash=${props.statementId}`)
                ]);
            }
            // @ts-ignore
            const [statementResponse, groupedStatementResponse, ...additionalResponses] = responses.length && responses.map(response => response?.data);

            if (isGroupStatement) {
                setGroupedStatementActivity(groupedStatementResponse?.map((item: Time) => [Helper.parseTimesliceWithTz(String(item.timeslice)).valueOf(), item.waittime]));
            } else {
                setStatementActivity(statementResponse?.map((item: Time) => [Helper.parseTimesliceWithTz(String(item.timeslice)).valueOf(), item.waittime]));
            }

            if (props.instanceType === INSTANCE_TYPE_SQLSERVER) {
                const [batchResponse, groupedBatchResponse] = additionalResponses;
                if (isGroupBatch) {
                    setGroupedBatchActivity(groupedBatchResponse?.map((item: Time) => [Helper.parseTimesliceWithTz(String(item.timeslice)).valueOf(), item.waittime]));
                } else {
                    setBatchActivity(batchResponse?.map((item: Time) => [Helper.parseTimesliceWithTz(String(item.timeslice)).valueOf(), item.waittime]));
                }
            }

            setTimeChartLoading(false);
        } catch (error) {
            setTimeChartLoading(false);
            console.error('Failed to retrieve activity time.', error);
        }
    };

    const fetchSqlStatistics = async(isGroupStatement: boolean) => {
        try {
            const statisticsResponses = await Promise.all([
                !isGroupStatement && SqlApi.getStatistic('time', buildApiQuery(timeRangeContext.timeRange), props.instanceId, props.instanceType, `&sqlhash=${props.statementId}`),
                isGroupStatement && SqlApi.getStatistic('time', buildApiQuery(timeRangeContext.timeRange), props.instanceId, props.instanceType, `&grouphash=${props.statementId}`)
            ]);

            // @ts-ignore
            const [statisticsResponse, groupedStatisticResponse] = statisticsResponses.map(response => response?.data);

            if (isGroupStatement) {
                setGroupedStatisticsTimeSeries(groupedStatisticResponse?.map((item: any) => [Helper.parseTimesliceWithTz(String(item.timeslice)).valueOf(), item.duration]));
                setGroupedExecutionsSeries(groupedStatisticResponse?.map((item: any) => [Helper.parseTimesliceWithTz(String(item.timeslice)).valueOf(), item.executions]));
            } else {
                setStatisticsTimeSeries(statisticsResponse?.map((item: any) => [Helper.parseTimesliceWithTz(String(item.timeslice)).valueOf(), item.duration]));
                setExecutionsSeries(statisticsResponse?.map((item: any) => [Helper.parseTimesliceWithTz(String(item.timeslice)).valueOf(), item.executions]));
            }

            setSqlChartLoading(false);
        } catch (error) {
            setSqlChartLoading(false);
            console.error('Failed to retrieve SQL Statistics.', error);
        }
    };

    const fetchData = async() => {
        try {
            const statementActivityResponse = await ActivityApi.get('sql', buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&grouphash=${props.statementId}`);
            // check if it ahs more the one statement ar is a grout with only one statement
            let isGroupStatement = statementActivityResponse?.data?.length > 1 || (statementActivityResponse?.data.length === 1 && statementActivityResponse?.data[0].sqlhash !== props.statementId);
            let isGroupBatch = false

            if (props.instanceType === INSTANCE_TYPE_SQLSERVER) {
                const batchActivityResponse = await ActivityApi.get('batch', buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&batchgrouphash=${props.statementId}`);
                isGroupBatch = batchActivityResponse?.data?.length > 1 || (batchActivityResponse?.data.length === 1 && batchActivityResponse?.data[0].batchsqlhash !== props.statementId);

                if (!batchActivityResponse?.data?.length) {
                    isGroupBatch = false
                }
            }


            if (!statementActivityResponse?.data?.length) {
                isGroupStatement = false
            }

            // Fetch statement activity
            void fetchStatementActivity(isGroupStatement, isGroupBatch);

            // Fetch SQL statistics
            void fetchSqlStatistics(isGroupStatement);

        } catch (error) {
            setTimeChartLoading(false);
            setSqlChartLoading(false);
            console.error('Error fetching data:', error);
        }
    };

    useMemo(() => {
        void fetchData();
    }, [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.instanceId, props.statementId, props.instanceType]);


    const noData = () => {
        return !timeChartLoading && !sqlChartLoading && !statisticsTimeSeries.length && !groupedStatisticsTimeSeries.length && !statementActivity.length && !groupedStatementActivity.length && !batchActivity.length && !groupedBatchActivity.length
    }

    return <div>
        <ConditionalRender if={noData()}>
            <div className="w-100 text-center text-muted my-3">
                <i className="fal fa-ban fa-fw fa-2x"></i>
                <p className="mt-3">
                    This SQL text has no performance data within the current period. Try expanding your time range
                </p>
            </div>
        </ConditionalRender>


        <ConditionalRender if={timeChartLoading}>
            <div className="w-100 text-center chart-center text-muted mt-3">
                <div className="loader spinner chartSpinner">
                </div>
                <p className="mt-3">
                    Loading data...
                </p>
            </div>
        </ConditionalRender>

        <ConditionalRender if={!timeChartLoading && !noData()}>
            <h3>Database Activity</h3>
            <div className='d-flex flex-wrap'>
                <ConditionalRender if={statementActivity.length}>
                    <SimpleTimeChart instanceId={props.instanceId}
                                     statementId={props.statementId}
                                    
                                     type='Statement'
                                     timeSeries={statementActivity}
                                     more={`/instances/${props.instanceId}/activity/statement/${props.statementId}/activity`}
                    />
                </ConditionalRender>

                <ConditionalRender if={groupedStatementActivity.length}>
                    <SimpleTimeChart instanceId={props.instanceId}
                                     statementId={props.statementId}
                                    
                                     type='Grouped Statement'
                                     timeSeries={groupedStatementActivity}
                                     more={`/instances/${props.instanceId}/activity/groupedstatement/${props.statementId}/activity`}
                    />
                </ConditionalRender>

                <ConditionalRender if={batchActivity.length}>
                    <SimpleTimeChart instanceId={props.instanceId}
                                     statementId={props.statementId}
                                    
                                     type='Batch'
                                     timeSeries={batchActivity}
                                     more={`/instances/${props.instanceId}/activity/batch/${props.statementId}/activity`}
                    />
                </ConditionalRender>

                <ConditionalRender if={groupedBatchActivity.length}>
                    <SimpleTimeChart instanceId={props.instanceId}
                                    
                                     statementId={props.statementId}
                                     type='Grouped Batch'
                                     timeSeries={groupedBatchActivity}
                                     more={`/instances/${props.instanceId}/activity/groupedbatch/${props.statementId}/activity`}
                    />
                </ConditionalRender>
                <ConditionalRender
                    if={!statementActivity.length && !groupedStatementActivity.length && !batchActivity.length && !groupedBatchActivity.length}>
                    <div className="w-100 text-center text-muted my-3">
                        <i className="fal fa-ban fa-fw fa-2x"></i>
                        <p className="mt-3">
                            No data found
                        </p>
                    </div>
                </ConditionalRender>
            </div>
        </ConditionalRender>

        <ConditionalRender if={sqlChartLoading}>
            <div className="w-100 text-center chart-center text-muted mt-3">
                <div className="loader spinner chartSpinner">
                </div>
                <p className="mt-3">
                    Loading data...
                </p>
            </div>
        </ConditionalRender>

        <ConditionalRender if={!sqlChartLoading && !noData()}>
            <h3>SQL Statistics</h3>
            <div className='d-flex flex-wrap'>
                <ConditionalRender if={statisticsTimeSeries.length}>
                    <SimpleTimeChart instanceId={props.instanceId}
                                     statementId={props.statementId}
                                     timeSeries={statisticsTimeSeries}
                                    
                                     type='Statement'
                                     executionsSeries={executionsSeries}
                                     more={`/instances/${props.instanceId}/sql-statistics/statement/${props.statementId}/sql-statistics`}
                    />
                </ConditionalRender>

                <ConditionalRender if={groupedStatisticsTimeSeries.length}>
                    <SimpleTimeChart instanceId={props.instanceId}
                                    
                                     statementId={props.statementId}
                                     type='Grouped Statement'
                                     timeSeries={groupedStatisticsTimeSeries}
                                     executionsSeries={groupedExecutionsSeries}
                                     more={`/instances/${props.instanceId}/activity/groupedstatement/${props.statementId}/sql-statistics`}
                    />
                </ConditionalRender>
                <ConditionalRender if={!statisticsTimeSeries.length && !groupedStatisticsTimeSeries.length}>
                    <div className="w-100 text-center text-muted my-3">
                        <i className="fal fa-ban fa-fw fa-2x"></i>
                        <p className="mt-3">
                            No data found
                        </p>
                    </div>
                </ConditionalRender>
            </div>
        </ConditionalRender>
    </div>
}
export default GenericActivity;
